import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconButton, Button, Select, MenuItem } from "@mui/material";
import {
  Menu,
  Search,
  CalendarDays,
  Filter,
  RefreshCw,
  UserRound,
  CheckCircle2,
  Eye,
  UserCheck,
  LayoutGrid,
  List as ListIcon,
  Phone,
  Mail,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import DoctorHeader from "../doctor_dashboard/DoctorHeader";
import DoctorFooter from "../doctor_dashboard/DoctorFooter";
import SimpleSnackbar from "../Snackbar";
import DoctorSidebar, { DoctorSidebarDrawer } from "../doctor_dashboard/DoctorSidebar";
import {
  getAdminPatientsCache,
  setAdminPatientsCache,
} from "../../cache/adminPatientsCache";

const PAGE_LIMIT = 30;

/* ================= HELPERS ================= */
const formatDate = (date) => {
  if (!date) return "—";

  return new Date(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getInitials = (name = "") => {
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((item) => item[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "PT"
  );
};

const getPaymentStatusMeta = (paymentStatus = {}) => {
  if (paymentStatus.activePaid) {
    return {
      label: paymentStatus.label || "Paid active",
      className: "bg-emerald-50 text-emerald-700",
    };
  }

  if (paymentStatus.hasPaidBefore) {
    return {
      label: paymentStatus.label || "Paid expired",
      className: "bg-amber-50 text-amber-700",
    };
  }

  return {
    label: paymentStatus.label || "Never paid",
    className: "bg-slate-100 text-slate-600",
  };
};

/* ================= PATIENT CARD ================= */
const PatientCard = ({
  patient,
  doctors,
  fetchPatients,
  setSnackMessage,
  setSnackColor,
  viewMode,
}) => {
  const [selectedDoctor, setSelectedDoctor] = useState(
    patient.assignedDoctor?.id || ""
  );

  const [assigning, setAssigning] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const navigate = useNavigate();

  const assignedDoctor = doctors.find(
    (doctor) => doctor._id === patient.assignedDoctor?.id
  );

  const isAssigned = !!patient.assignedDoctor?.id;
  const isAccepted = !!patient.assignedDoctor?.accepted;
  const paymentMeta = getPaymentStatusMeta(patient.paymentStatus);
  const phoneNumber = patient.phoneNumber || patient.phone || "No phone number";
  const email = patient.email || "No email address";

  const assignDoctor = async () => {
    if (!selectedDoctor) {
      setSnackMessage("Please select a doctor");
      setSnackColor("error");
      return;
    }

    try {
      setAssigning(true);

      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/admin/assign-doctor`,
        {
          patientId: patient._id,
          doctorId: selectedDoctor,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (res.data.success) {
        setSnackMessage("Doctor assigned");
        setSnackColor("success");

        fetchPatients({
          pageNumber: 1,
          append: false,
          silent: true,
        });
      }
    } catch (error) {
      console.error(error);
      setSnackMessage("Failed to assign doctor");
      setSnackColor("error");
    } finally {
      setAssigning(false);
    }
  };

  const acceptPatient = async () => {
    try {
      setAccepting(true);

      const doctor = JSON.parse(localStorage.getItem("doctor") || "{}");

      const res = await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/clients-accept/${patient._id}`,
        { doctorId: doctor._id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (res.data.success) {
        setSnackMessage("Patient accepted");
        setSnackColor("success");
        fetchPatients({
          pageNumber: 1,
          append: false,
          silent: true,
        });
      }
    } catch (error) {
      console.error(error);
      setSnackMessage("Failed to accept patient");
      setSnackColor("error");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <article
      className={`rounded-2xl border border-slate-200 bg-white transition hover:border-slate-300 hover:shadow-sm ${
        viewMode === "list" ? "p-4" : "p-5"
      }`}
    >
      {/* Top */}
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-900 text-sm font-semibold text-white">
          {getInitials(patient.name)}
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-semibold text-slate-950">
            {patient.name || "Unnamed Patient"}
          </h4>

          <p className="mt-1 text-xs text-slate-500">
            {patient.age || "—"} yrs · {patient.sex || "—"}
          </p>

          <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            <Phone size={12} />
            {phoneNumber}
          </p>

          <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
            <Mail size={12} />
            <span className="truncate">{email}</span>
          </p>

          <p className="mt-1 text-xs text-slate-400">
            Registered {formatDate(patient.createdAt)}
          </p>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
              isAccepted
                ? "bg-emerald-50 text-emerald-700"
                : isAssigned
                ? "bg-amber-50 text-amber-700"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            {isAccepted ? "Accepted" : isAssigned ? "Assigned" : "Unassigned"}
          </span>

          <span
            className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${paymentMeta.className}`}
          >
            {paymentMeta.label}
          </span>
        </div>
      </div>

      {/* Complaint */}
      <div className="mt-5">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-400">
          Complaint
        </p>

        <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-700">
          {patient.chiefComplaint || "No complaint recorded"}
        </p>
      </div>

      {/* Assigned doctor */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-medium text-slate-400">Assigned doctor</p>

        <p className="mt-1 truncate text-sm font-medium text-slate-800">
          {assignedDoctor?.name ||
            patient.assignedDoctor?.name ||
            "No doctor assigned"}
        </p>
      </div>

      {/* Select */}
      <div className="mt-5">
        <label className="mb-2 block text-xs font-medium text-slate-500">
          Assign doctor
        </label>

        <Select
          value={selectedDoctor}
          onChange={(e) => setSelectedDoctor(e.target.value)}
          size="small"
          fullWidth
          displayEmpty
          sx={{
            borderRadius: "12px",
            fontSize: "14px",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#e2e8f0",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#cbd5e1",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#334155",
            },
          }}
        >
          <MenuItem value="">Select doctor</MenuItem>

          {doctors.map((doctor) => (
            <MenuItem key={doctor._id} value={doctor._id}>
              {doctor.name}
            </MenuItem>
          ))}
        </Select>
      </div>

      {/* Actions */}
      <div className="mt-6 grid grid-cols-1 gap-2">
        <Button
          size="small"
          variant="contained"
          onClick={assignDoctor}
          disabled={assigning || !selectedDoctor}
          startIcon={<UserCheck size={15} />}
          sx={{
            minHeight: 42,
            bgcolor: "#075E54",
            color: "white",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#064b43",
              boxShadow: "none",
            },
            "&:disabled": {
              bgcolor: "#e2e8f0",
              color: "#64748b",
            },
          }}
        >
          {assigning ? "Assigning..." : "Assign doctor"}
        </Button>

        {isAssigned && !isAccepted && (
          <Button
            size="small"
            variant="outlined"
            onClick={acceptPatient}
            disabled={accepting}
            startIcon={<CheckCircle2 size={15} />}
            sx={{
              minHeight: 42,
              borderColor: "#e2e8f0",
              color: "#0f172a",
              borderRadius: "12px",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": {
                borderColor: "#cbd5e1",
                bgcolor: "#f8fafc",
                boxShadow: "none",
              },
            }}
          >
            {accepting ? "Accepting..." : "Accept patient"}
          </Button>
        )}

        <Button
          size="small"
          variant="outlined"
          onClick={() => navigate("/admin/chat/" + patient._id)}
          startIcon={<Eye size={15} />}
          sx={{
            minHeight: 42,
            borderColor: "#e2e8f0",
            color: "#334155",
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 600,
            boxShadow: "none",
            "&:hover": {
              borderColor: "#cbd5e1",
              bgcolor: "#f8fafc",
              boxShadow: "none",
            },
          }}
        >
          View patient
        </Button>
      </div>
    </article>
  );
};

/* ================= SKELETON ================= */
const PatientCardSkeleton = () => {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="animate-pulse">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-full bg-slate-200" />

          <div className="flex-1">
            <div className="h-4 w-32 rounded bg-slate-200" />
            <div className="mt-2 h-3 w-20 rounded bg-slate-100" />
            <div className="mt-2 h-3 w-24 rounded bg-slate-100" />
          </div>
        </div>

        <div className="mt-6 h-3 w-24 rounded bg-slate-200" />
        <div className="mt-3 h-3 w-full rounded bg-slate-100" />
        <div className="mt-2 h-3 w-4/5 rounded bg-slate-100" />

        <div className="mt-6 h-11 w-full rounded-xl bg-slate-200" />
        <div className="mt-2 h-11 w-full rounded-xl bg-slate-100" />
      </div>
    </div>
  );
};

/* ================= MAIN ================= */
const AdminPatientsPage = () => {
  const cachedAdminPatients = getAdminPatientsCache();

  const [patients, setPatients] = useState(
    cachedAdminPatients?.patients || []
  );
  const [doctors, setDoctors] = useState(cachedAdminPatients?.doctors || []);
  const patientsRef = useRef(cachedAdminPatients?.patients || []);
  const doctorsRef = useRef(cachedAdminPatients?.doctors || []);

  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(
    !(cachedAdminPatients?.patients || []).length
  );
  const [loadingMore, setLoadingMore] = useState(false);

  const [snackMessage, setSnackMessage] = useState("");
  const [snackColor, setSnackColor] = useState("success");

  const [drawerOpen, setDrawerOpen] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("adminPatientsViewMode") || "grid";
  });

  const [page, setPage] = useState(cachedAdminPatients?.page || 1);
  const [totalPatients, setTotalPatients] = useState(
    cachedAdminPatients?.totalPatients || 0
  );
  const [hasNextPage, setHasNextPage] = useState(
    cachedAdminPatients?.hasNextPage ?? true
  );
  const hasVisiblePatientsRef = useRef(
    !!(cachedAdminPatients?.patients || []).length
  );
  const paginationRef = useRef({
    totalPatients: cachedAdminPatients?.totalPatients || 0,
    page: cachedAdminPatients?.page || 1,
    hasNextPage: cachedAdminPatients?.hasNextPage ?? true,
  });

  const navigate = useNavigate();
  const location = useLocation();

  const activeFiltersCount = useMemo(() => {
    return [
      searchName,
      startDate,
      endDate,
      assignedFilter,
      selectedDoctor,
      paymentFilter,
    ].filter(Boolean).length;
  }, [
    searchName,
    startDate,
    endDate,
    assignedFilter,
    selectedDoctor,
    paymentFilter,
  ]);

  /* ================= FETCH PATIENTS ================= */
  const fetchPatients = useCallback(
    async ({
      pageNumber = 1,
      append = false,
      silent = false,
    } = {}) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else if (!silent && !hasVisiblePatientsRef.current) {
          setLoading(true);
        }

        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/admin/patients`,
          {
            name: searchName,
            startDate,
            endDate,
            assigned: assignedFilter,
            doctorId: selectedDoctor,
            paymentStatus: paymentFilter,
            page: pageNumber,
            limit: PAGE_LIMIT,
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (res.data.success) {
          const nextPatients = res.data.patients || [];
          const pagination = res.data.pagination || {};

          setTotalPatients(pagination.total || 0);
          setPage(pagination.page || pageNumber);
          setHasNextPage(!!pagination.hasNextPage);
          paginationRef.current = {
            totalPatients: pagination.total || 0,
            page: pagination.page || pageNumber,
            hasNextPage: !!pagination.hasNextPage,
          };

          setPatients((prev) => {
            const mergedPatients = append
              ? [...prev, ...nextPatients]
              : nextPatients;

            patientsRef.current = mergedPatients;
            hasVisiblePatientsRef.current = mergedPatients.length > 0;

            setAdminPatientsCache({
              patients: mergedPatients,
              doctors: doctorsRef.current,
              ...paginationRef.current,
            });

            return mergedPatients;
          });
        }
      } catch (err) {
        console.error(err);
        setSnackMessage("Failed to load patients");
        setSnackColor("error");
      } finally {
        setLoading(false);
        setLoadingMore(false);
        setInitialLoad(false);
      }
    },
    [
      searchName,
      startDate,
      endDate,
      assignedFilter,
      selectedDoctor,
      paymentFilter,
    ]
  );

  /* ================= FETCH DOCTORS ================= */
  const fetchDoctors = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/doctors`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const nextDoctors = res.data.doctors || [];

      setDoctors(nextDoctors);
      doctorsRef.current = nextDoctors;
      setAdminPatientsCache({
        patients: patientsRef.current,
        doctors: nextDoctors,
        ...paginationRef.current,
      });
    } catch (error) {
      console.error(error);
      setSnackMessage("Failed to load doctors");
      setSnackColor("error");
    }
  }, []);

  /* ================= URL INIT ================= */
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    setSearchName(params.get("name") || "");
    setStartDate(params.get("startDate") || "");
    setEndDate(params.get("endDate") || "");
    setAssignedFilter(params.get("assigned") || "");
    setSelectedDoctor(params.get("doctorId") || "");
    setPaymentFilter(params.get("paymentStatus") || "");
  }, [location.search]);

  /* ================= URL SYNC ================= */
  useEffect(() => {
    const params = new URLSearchParams();

    if (searchName) params.set("name", searchName);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    if (assignedFilter) params.set("assigned", assignedFilter);
    if (selectedDoctor) params.set("doctorId", selectedDoctor);
    if (paymentFilter) params.set("paymentStatus", paymentFilter);

    navigate({ search: params.toString() }, { replace: true });
  }, [
    searchName,
    startDate,
    endDate,
    assignedFilter,
    selectedDoctor,
    paymentFilter,
    navigate,
  ]);

  useEffect(() => {
    localStorage.setItem("adminPatientsViewMode", viewMode);
  }, [viewMode]);

  /* ================= INIT DOCTORS ================= */
  useEffect(() => {
    fetchDoctors();
  }, [fetchDoctors]);

  /* ================= FETCH WHEN FILTERS CHANGE ================= */
  useEffect(() => {
    setPage(1);
    setHasNextPage(true);

    fetchPatients({
      pageNumber: 1,
      append: false,
      silent: false,
    });
  }, [fetchPatients]);

  /* ================= SILENT POLLING ================= */
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPatients({
        pageNumber: 1,
        append: false,
        silent: true,
      });
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchPatients]);

  const clearFilters = () => {
    setSearchName("");
    setStartDate("");
    setEndDate("");
    setAssignedFilter("");
    setSelectedDoctor("");
    setPaymentFilter("");
  };

  const loadMore = () => {
    if (!hasNextPage || loadingMore) return;

    fetchPatients({
      pageNumber: page + 1,
      append: true,
      silent: true,
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <DoctorHeader />

      {/* Mobile Header */}
      <div className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
        <div>
          <p className="text-xs text-slate-400">Admin</p>
          <h1 className="text-base font-semibold text-slate-950">
            All Patients
          </h1>
        </div>

        <IconButton onClick={() => setDrawerOpen(true)}>
          <Menu />
        </IconButton>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className="hidden lg:flex">
          <DoctorSidebar activeTab="Admin" setActiveTab={() => {}} />
        </div>

        <DoctorSidebarDrawer
          open={drawerOpen}
          onOpen={() => setDrawerOpen(true)}
          onClose={() => setDrawerOpen(false)}
          activeTab="Admin"
          setActiveTab={() => {}}
        />

        {/* Main */}
        <main className="relative flex-1 overflow-y-auto">
        

          <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
            {/* Page title */}
            <div className="mb-8 flex flex-col gap-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-[-0.03em] text-slate-950 sm:text-3xl">
                    All Patients
                  </h2>

                  <p className="mt-2 text-sm text-slate-500">
                    {Number(totalPatients || 0).toLocaleString()} total patients
                  </p>
                </div>

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  >
                    <Filter size={16} />
                    Clear
                    {activeFiltersCount > 0 && (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() =>
                      fetchPatients({
                        pageNumber: 1,
                        append: false,
                        silent: true,
                      })
                    }
                    className="inline-flex h-11 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
                  >
                    <RefreshCw size={16} />
                    Refresh
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Total patients</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {Number(totalPatients || 0).toLocaleString()}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Loaded</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {Number(patients.length || 0).toLocaleString()}
                  </p>
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white p-5">
                  <p className="text-sm text-slate-500">Doctors</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-950">
                    {Number(doctors.length || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Filters */}
            <section className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-6">
                <div className="xl:col-span-2">
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Search
                  </label>

                  <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <Search size={16} className="text-slate-400" />

                    <input
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      placeholder="Patient name"
                      className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Start date
                  </label>

                  <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <CalendarDays size={16} className="text-slate-400" />

                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-transparent text-sm text-slate-800 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    End date
                  </label>

                  <div className="flex h-11 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                    <CalendarDays size={16} className="text-slate-400" />

                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-transparent text-sm text-slate-800 outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Assignment
                  </label>

                  <select
                    value={assignedFilter}
                    onChange={(e) => setAssignedFilter(e.target.value)}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none"
                  >
                    <option value="">All</option>
                    <option value="assigned">Assigned</option>
                    <option value="unassigned">Unassigned</option>
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Payment
                  </label>

                  <select
                    value={paymentFilter}
                    onChange={(e) => setPaymentFilter(e.target.value)}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none"
                  >
                    <option value="">All payments</option>
                    <option value="paid_active">Paid now</option>
                    <option value="paid_before">Paid before</option>
                    <option value="paid_expired">Paid before, expired</option>
                    <option value="never_paid">Never paid</option>
                  </select>
                </div>

                <div className="xl:col-span-2">
                  <label className="mb-2 block text-xs font-medium text-slate-500">
                    Doctor
                  </label>

                  <select
                    value={selectedDoctor}
                    onChange={(e) => setSelectedDoctor(e.target.value)}
                    className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none"
                  >
                    <option value="">All doctors</option>

                    {doctors.map((doctor) => (
                      <option key={doctor._id} value={doctor._id}>
                        {doctor.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

           

            {/* Patient title */}
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="text-base font-semibold text-slate-950">
                Patients
              </h3>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <p className="text-sm text-slate-500">
                  Showing {patients.length.toLocaleString()} of{" "}
                  {Number(totalPatients || 0).toLocaleString()}
                </p>

                <div className="inline-flex w-fit rounded-xl border border-slate-200 bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("grid")}
                    className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium transition ${
                      viewMode === "grid"
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <LayoutGrid size={15} />
                    Grid
                  </button>

                  <button
                    type="button"
                    onClick={() => setViewMode("list")}
                    className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium transition ${
                      viewMode === "list"
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <ListIcon size={15} />
                    List
                  </button>
                </div>
              </div>
            </div>

            {loading && patients.length === 0 && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
                Loading patients...
              </div>
            )}

            {/* Empty */}
            {!loading && patients.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                  <UserRound size={20} className="text-slate-500" />
                </div>

                <h3 className="text-base font-semibold text-slate-950">
                  No patients found
                </h3>

                <p className="mx-auto mt-2 max-w-sm text-sm leading-6 text-slate-500">
                  Try changing your search or clearing the filters.
                </p>
              </div>
            ) : (
              <>
                <div
                  className={
                    viewMode === "list"
                      ? "grid grid-cols-1 gap-3"
                      : "grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
                  }
                >
                  {patients.map((patient) => (
                    <PatientCard
                      key={patient._id}
                      patient={patient}
                      doctors={doctors}
                      fetchPatients={fetchPatients}
                      setSnackMessage={setSnackMessage}
                      setSnackColor={setSnackColor}
                      viewMode={viewMode}
                    />
                  ))}

                  {loading &&
                    initialLoad &&
                    Array.from({ length: 6 }).map((_, index) => (
                      <PatientCardSkeleton key={index} />
                    ))}
                </div>

                {hasNextPage && (
                  <div className="flex justify-center py-8">
                    <Button
                      onClick={loadMore}
                      disabled={loadingMore}
                      sx={{
                        textTransform: "none",
                        borderRadius: "12px",
                        px: 4,
                        py: 1.2,
                        bgcolor: "#075E54",
                        color: "white",
                        fontWeight: 600,
                        boxShadow: "none",
                        "&:hover": {
                          bgcolor: "#064b43",
                          boxShadow: "none",
                        },
                        "&:disabled": {
                          bgcolor: "#e2e8f0",
                          color: "#64748b",
                        },
                      }}
                    >
                      {loadingMore ? "Loading..." : "Load more"}
                    </Button>
                  </div>
                )}

                {!hasNextPage && patients.length > 0 && (
                  <div className="py-8 text-center text-sm text-slate-400">
                    End of list
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>


      <SimpleSnackbar
        message={snackMessage}
        color={snackColor}
        onClose={() => setSnackMessage("")}
      />
    </div>
  );
};

export default AdminPatientsPage;
